import { FC, useMemo } from "react";
import { useGetVessels } from "~/services/vessel";
import { useGetServiceTickets } from "~/services/service-ticket";
import { VesselLoadListCard } from "./vessel-load-list-card";
import {
  SortBy,
  useVesselLoadListStore,
} from "../../operations/vessels-shuttles/store/vesselLoadList.store";
import { ServiceType } from "~/utils/constants";
import dayjs from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import { Spin, Empty } from "antd";

// Add weekOfYear plugin to dayjs
dayjs.extend(weekOfYear);

type Props = {};

export const ListVessels: FC<Props> = () => {
  const { filters } = useVesselLoadListStore();
  
  // Get all vessels
  const {
    data: vessels,
    isLoading: isLoadingVessels,
    error: errorVessels,
    refetch: refetchVessel,
  } = useGetVessels({ getAll: true });
  
  // Get service tickets for the selected date (deliveries + warehouse with RELEASE)
  const {
    data: serviceTickets,
    isLoading: isLoadingTickets,
    error: errorTickets,
  } = useGetServiceTickets({
    startDate: filters.startDate,
    endDate: filters.endDate,
    vesselId: filters.vesselId,
    include: 'inventories', // Try to include inventories
  });

  const isLoading = isLoadingVessels || isLoadingTickets;
  const error = errorVessels || errorTickets;

  // Create vessels with their service tickets for the selected date
  const sortedVessels = useMemo(() => {
    console.log('Vessels data:', vessels);
    console.log('Service tickets data:', serviceTickets);
    if (!vessels?.items || !serviceTickets?.items) return [];

    // Filter for delivery services and warehouse services with RELEASE add-on
    const relevantServices = serviceTickets.items.filter(ticket => {
      const isDelivery = ticket.serviceType === ServiceType.DELIVERY;
      const isWarehouseWithRelease =
        ticket.serviceType === ServiceType.WAREHOUSE_SERVICE &&
        ticket.addOnServices?.some((addOn: any) =>
          addOn.name?.toLowerCase().includes('release')
        );
      return isDelivery || isWarehouseWithRelease;
    });

    // Group delivery services by vessel
    const vesselsWithServices = vessels.items
      .map(vessel => {
        // Get delivery service tickets for this vessel
        const vesselServices = relevantServices.filter(ticket =>
          ticket.vessel?.id === vessel.id && ticket.serviceType === ServiceType.DELIVERY
        );

        // Only return vessels with services
        if (vesselServices.length === 0) {
          return null;
        }

        // Add service tickets to the vessel object
        const vesselWithServices = {
          ...vessel,
          serviceTickets: vesselServices,
        };
        console.log('Vessel with services:', vesselWithServices);
        console.log('Vessel services inventories:', vesselServices.map(s => ({ id: s.id, inventories: s.inventories })));
        return vesselWithServices;
      })
      .filter(Boolean);

    // Create a "Warehouse - Release" pseudo-vessel for warehouse services with RELEASE
    const warehouseReleaseServices = relevantServices.filter(ticket =>
      ticket.serviceType === ServiceType.WAREHOUSE_SERVICE
    );

    if (warehouseReleaseServices.length > 0) {
      vesselsWithServices.push({
        id: 'warehouse-release',
        name: 'Warehouse - Release',
        serviceTickets: warehouseReleaseServices,
        isWarehouseRelease: true, // Flag to identify this special group
      } as any);
    }

    // Sort vessels based on the selected sort option
    const items = [...vesselsWithServices];

    switch (filters.sortBy) {
      case SortBy.DATE:
        return items.sort((a, b) => {
          const aDate = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          const bDate = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
          return bDate - aDate;
        });

      case SortBy.WEEK:
        return items.sort((a, b) => {
          const aWeek = a.updatedAt ? dayjs(a.updatedAt).week() : 0;
          const bWeek = b.updatedAt ? dayjs(b.updatedAt).week() : 0;
          return bWeek - aWeek;
        });

      case SortBy.VESSEL:
        return items.sort((a, b) => a.name.localeCompare(b.name));

      default:
        return items;
    }
  }, [vessels?.items, serviceTickets?.items, filters.sortBy]);

  if (isLoading) {
    return (
      <div className="size-full flex items-center justify-center">
        <Spin size="large" tip="Loading vessels..." />
      </div>
    );
  }

  if (error) {
    return <div>Error loading vessels: {(error as Error).message}</div>;
  }

  if (!sortedVessels || sortedVessels.length === 0) {
    return (
      <div className="bg-white rounded-lg border shadow-sm p-8">
        <Empty
          description="No delivery services or warehouse releases scheduled for the selected date. Try selecting a different date from the calendar."
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {sortedVessels.map((vessel) => (
        <VesselLoadListCard
          key={vessel.id}
          vessel={vessel}
        />
      ))}
    </div>
  );
};
